import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { usePortalTour } from '../../context/PortalTourContext';
import { useOrg } from '../../context/OrgContext';
import { getChapterState } from '../../config/portalTourSteps';
import { Button } from '../ui/button';
import {
  X,
  ChevronLeft,
  ChevronRight,
  Sparkles,
  LayoutDashboard,
  Users,
  UserCheck,
  Heart,
  CalendarClock,
  Receipt,
  MapPin,
  Landmark,
  Briefcase,
  HeartHandshake,
  Building2,
  MessageSquare,
  GraduationCap,
  Shield,
  History,
  UserCog,
  Settings,
  Search,
  UserPlus,
  CircleCheck,
  Eye,
  ShieldCheck,
  ClipboardCheck,
  ListChecks,
  CalendarRange,
  FilePlus,
  Lock,
  Clock,
} from 'lucide-react';

const TOOLTIP_MARGIN = 12;
const VIEWPORT_MARGIN = 16;
const TOOLTIP_MAX_WIDTH = 400;
const TOUR_Z_INDEX = 9999;

const STEP_ICONS = {
  sparkles: Sparkles,
  'layout-dashboard': LayoutDashboard,
  users: Users,
  'user-check': UserCheck,
  heart: Heart,
  'calendar-clock': CalendarClock,
  receipt: Receipt,
  'map-pin': MapPin,
  landmark: Landmark,
  briefcase: Briefcase,
  'heart-handshake': HeartHandshake,
  'building-2': Building2,
  'message-square': MessageSquare,
  'graduation-cap': GraduationCap,
  shield: Shield,
  history: History,
  'user-cog': UserCog,
  settings: Settings,
  search: Search,
  'user-plus': UserPlus,
  'circle-check': CircleCheck,
  eye: Eye,
  'shield-check': ShieldCheck,
  'clipboard-check': ClipboardCheck,
  'list-checks': ListChecks,
  'calendar-range': CalendarRange,
  'file-plus': FilePlus,
  lock: Lock,
};

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

function getTooltipLayout(rect, placement, tooltipSize, isCenter) {
  const { width, height } = tooltipSize;

  if (isCenter || !rect) {
    return { maxWidth: TOOLTIP_MAX_WIDTH };
  }

  const placements = [placement, 'bottom', 'top', 'right', 'left'].filter(
    (value, index, list) => list.indexOf(value) === index,
  );

  for (const candidate of placements) {
    let top = 0;
    let left = 0;

    switch (candidate) {
      case 'left':
        top = rect.top + rect.height / 2 - height / 2;
        left = rect.left - TOOLTIP_MARGIN - width;
        break;
      case 'top':
        top = rect.top - TOOLTIP_MARGIN - height;
        left = rect.left + rect.width / 2 - width / 2;
        break;
      case 'bottom':
        top = rect.bottom + TOOLTIP_MARGIN;
        left = rect.left + rect.width / 2 - width / 2;
        break;
      case 'right':
      default:
        top = rect.top + rect.height / 2 - height / 2;
        left = rect.right + TOOLTIP_MARGIN;
        break;
    }

    const fits =
      left >= VIEWPORT_MARGIN &&
      top >= VIEWPORT_MARGIN &&
      left + width <= window.innerWidth - VIEWPORT_MARGIN &&
      top + height <= window.innerHeight - VIEWPORT_MARGIN;

    if (fits) {
      return {
        top: clamp(top, VIEWPORT_MARGIN, window.innerHeight - height - VIEWPORT_MARGIN),
        left: clamp(left, VIEWPORT_MARGIN, window.innerWidth - width - VIEWPORT_MARGIN),
        maxWidth: TOOLTIP_MAX_WIDTH,
      };
    }
  }

  return {
    top: clamp(
      rect.top + rect.height / 2 - height / 2,
      VIEWPORT_MARGIN,
      window.innerHeight - height - VIEWPORT_MARGIN,
    ),
    left: clamp(
      rect.right + TOOLTIP_MARGIN,
      VIEWPORT_MARGIN,
      window.innerWidth - width - VIEWPORT_MARGIN,
    ),
    maxWidth: TOOLTIP_MAX_WIDTH,
  };
}

function StepIcon({ name, className }) {
  const Icon = STEP_ICONS[name] || Sparkles;
  return <Icon className={className} />;
}

export default function PortalTourOverlay() {
  const {
    isActive,
    step,
    stepIndex,
    stepCount,
    steps,
    targetReady,
    goNext,
    goBack,
    skipTour,
    activeTourId,
  } = usePortalTour();

  const { orgName } = useOrg();
  const [rect, setRect] = useState(null);
  const tooltipRef = useRef(null);
  const [tooltipSize, setTooltipSize] = useState({ width: TOOLTIP_MAX_WIDTH, height: 300 });

  useEffect(() => {
    if (!isActive || !step || step.disableSpotlight) {
      setRect(null);
      return undefined;
    }

    const update = () => {
      const el = document.querySelector(step.target);
      if (el) setRect(el.getBoundingClientRect());
    };

    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);

    const interval = targetReady ? null : window.setInterval(update, 100);

    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
      if (interval) window.clearInterval(interval);
    };
  }, [isActive, step, stepIndex, targetReady]);

  useLayoutEffect(() => {
    if (!isActive || !tooltipRef.current) return;
    const { width, height } = tooltipRef.current.getBoundingClientRect();
    setTooltipSize((prev) =>
      Math.abs(prev.width - width) > 1 || Math.abs(prev.height - height) > 1
        ? { width, height }
        : prev,
    );
  }, [isActive, step, stepIndex, rect, targetReady]);

  useEffect(() => {
    if (!isActive) return undefined;

    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        skipTour();
      } else if (event.key === 'ArrowRight' || event.key === 'Enter') {
        event.preventDefault();
        goNext();
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        if (stepIndex > 0) goBack();
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isActive, stepIndex, goNext, goBack, skipTour]);

  if (!isActive || !step) return null;

  const isCenter = step.disableSpotlight || step.placement === 'center';
  const waitingForTarget = !isCenter && !rect;
  const useCenterLayout = isCenter || waitingForTarget;
  const hasSpotlight = !isCenter && rect;
  const tooltipStyle = getTooltipLayout(
    rect,
    step.placement || 'right',
    tooltipSize,
    useCenterLayout,
  );

  const variant = step.variant || 'default';
  const isWelcome = variant === 'welcome';
  const isFinish = variant === 'finish';
  const { chapters, activeChapterIndex, progress } = getChapterState(steps, stepIndex);

  const tourLabel = activeTourId === 'payroll' ? 'Weekly payroll guide' : 'Portal guide';
  const accentClass = isFinish
    ? 'from-success/80 via-success to-success/90'
    : 'from-primary via-primary to-secondary';

  return createPortal(
    <div
      className={
        useCenterLayout
          ? 'portal-tour-root fixed inset-0 flex items-center justify-center p-4 pointer-events-none'
          : 'portal-tour-root fixed inset-0 pointer-events-none'
      }
      style={{ zIndex: TOUR_Z_INDEX }}
      role="dialog"
      aria-modal="true"
      aria-label={tourLabel}
    >
      {(isCenter || !hasSpotlight) && (
        <div
          className="portal-tour-backdrop absolute inset-0 bg-slate-900/45 backdrop-blur-[2px] pointer-events-auto"
          aria-hidden="true"
          onClick={skipTour}
        />
      )}

      <div
        ref={tooltipRef}
        key={`${activeTourId}-${stepIndex}`}
        className={
          useCenterLayout
            ? 'portal-tour-card portal-tour-card--centered relative z-[1] pointer-events-auto w-[min(400px,calc(100vw-2rem))] overflow-hidden rounded-2xl border border-[#E4E8EB]/80 bg-white shadow-[0_24px_64px_rgba(15,92,94,0.18)]'
            : 'portal-tour-card absolute z-[1] pointer-events-auto overflow-hidden rounded-2xl border border-[#E4E8EB]/80 bg-white shadow-[0_24px_64px_rgba(15,92,94,0.18)]'
        }
        style={
          useCenterLayout
            ? tooltipStyle
            : { ...tooltipStyle, width: 'min(400px, calc(100vw - 2rem))' }
        }
      >
        <div className={`h-1 w-full bg-gradient-to-r ${accentClass}`} aria-hidden="true" />

        <div className="p-5">
          <div className="flex items-start justify-between gap-3 mb-3">
            <div className="flex items-center gap-3 min-w-0">
              <div
                className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${
                  isFinish ? 'bg-success/10 text-success' : 'bg-accent text-primary'
                }`}
              >
                <StepIcon name={step.icon} className="h-5 w-5" />
              </div>
              <div className="min-w-0">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-text-muted truncate">
                  {tourLabel}
                </p>
                <p className="text-xs text-text-muted">
                  Step {stepIndex + 1} of {stepCount}
                  {step.estimatedMinutes ? (
                    <span className="inline-flex items-center gap-1 ml-2 text-text-muted/80">
                      <Clock className="h-3 w-3" />
                      ~{step.estimatedMinutes} min
                    </span>
                  ) : null}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={skipTour}
              className="text-text-muted hover:text-text-primary p-1.5 rounded-lg hover:bg-[#F8FAFA] shrink-0"
              aria-label="Close tour"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {chapters.length > 1 && (
            <div className="mb-4">
              <div className="flex items-center justify-between gap-2 mb-2">
                {chapters.map((chapter, index) => (
                  <div key={chapter.id} className="flex flex-1 flex-col items-center gap-1 min-w-0">
                    <div
                      className={`h-1.5 w-full rounded-full transition-colors ${
                        index < activeChapterIndex
                          ? 'bg-primary'
                          : index === activeChapterIndex
                            ? 'bg-primary/70'
                            : 'bg-[#E4E8EB]'
                      }`}
                      aria-hidden="true"
                    />
                    <span
                      className={`text-[10px] font-medium truncate w-full text-center ${
                        index === activeChapterIndex ? 'text-primary' : 'text-text-muted'
                      }`}
                    >
                      {chapter.label}
                    </span>
                  </div>
                ))}
              </div>
              <div className="h-1 w-full rounded-full bg-[#E4E8EB] overflow-hidden" aria-hidden="true">
                <div
                  className="h-full rounded-full bg-gradient-to-r from-primary to-secondary transition-all duration-300 ease-out"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          )}

          {step.showChapterIntro && step.chapterIntro && (
            <p className="text-xs font-medium text-primary mb-2">{step.chapterIntro}</p>
          )}

          {isWelcome ? (
            <>
              <p className="text-xs font-medium text-primary mb-1">{orgName || 'Your organisation'}</p>
              <h3 className="font-heading font-semibold text-text-primary text-xl leading-snug mb-2">
                {step.title}
              </h3>
            </>
          ) : (
            <h3
              className={`font-heading font-semibold text-text-primary leading-snug mb-2 ${
                isFinish ? 'text-xl' : 'text-lg'
              }`}
            >
              {step.title}
            </h3>
          )}

          <p className="text-sm text-text-muted leading-relaxed mb-4">{step.content}</p>

          {isWelcome && (
            <p className="text-xs text-text-muted mb-4 rounded-lg bg-[#F8FAFA] border border-[#E4E8EB] px-3 py-2">
              Press <kbd className="font-mono text-[10px] bg-white px-1 rounded border">Esc</kbd> to skip
              · <kbd className="font-mono text-[10px] bg-white px-1 rounded border">→</kbd> to continue
            </p>
          )}

          <div className="flex flex-wrap items-center justify-between gap-2 pt-1 border-t border-[#E4E8EB]/80">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={skipTour}
              className="text-text-muted hover:text-text-primary"
            >
              Skip for now
            </Button>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={goBack}
                disabled={stepIndex === 0}
                className="rounded-xl"
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                Back
              </Button>
              <Button
                type="button"
                size="sm"
                onClick={goNext}
                className={`rounded-xl text-white ${isFinish ? 'bg-success hover:bg-success/90' : 'bg-primary hover:bg-primary-hover'}`}
              >
                {isFinish ? 'Done' : stepIndex >= stepCount - 1 ? 'Finish' : 'Next'}
                {!isFinish && stepIndex < stepCount - 1 && <ChevronRight className="h-4 w-4 ml-1" />}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}